import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FolderService } from "@/services/database/FolderService";
import { ApiService } from "@/services/api/ApiService";
import {
  DocumentFolder,
  DocumentFolderWithCount,
  CreateDocumentFolderInput,
  UpdateDocumentFolderInput,
} from "@/types/knowledge/document-folder";

interface UseFoldersOptions {
  userId?: string;
  onCreateSuccess?: (folder: DocumentFolderWithCount) => void;
  onUpdateSuccess?: (folder: DocumentFolderWithCount) => void;
  onDeleteSuccess?: (folderId: string) => void;
}

export const useFolders = (options: UseFoldersOptions = {}) => {
  const queryClient = useQueryClient();

  // Query to fetch all folders
  const foldersQuery = useQuery({
    queryKey: ["document-folders", options.userId],
    queryFn: () => FolderService.getFolders(options.userId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation to create a folder
  const createFolderMutation = useMutation({
    mutationFn: (input: CreateDocumentFolderInput) => 
      FolderService.createFolder(input, options.userId),
    onSuccess: (folder) => {
      queryClient.invalidateQueries({ queryKey: ["document-folders"] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast.success(`Folder "${folder?.name}" created successfully`);
      if (folder && options.onCreateSuccess) {
        options.onCreateSuccess(folder as DocumentFolderWithCount);
      }
    },
    onError: (error) => {
      console.error("Error creating folder:", error);
      toast.error("Failed to create folder");
    },
  });

  // Mutation to update a folder
  const updateFolderMutation = useMutation({
    mutationFn: ({ folderId, input }: { folderId: string; input: UpdateDocumentFolderInput }) =>
      FolderService.updateFolder(folderId, input),
    onSuccess: (folder) => {
      queryClient.invalidateQueries({ queryKey: ["document-folders"] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast.success(`Folder "${folder?.name}" updated successfully`);
      if (folder && options.onUpdateSuccess) {
        options.onUpdateSuccess(folder as DocumentFolderWithCount);
      }
    },
    onError: (error) => {
      console.error("Error updating folder:", error);
      toast.error("Failed to update folder");
    },
  });

  // Mutation to delete a folder
  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: string) => FolderService.deleteFolder(folderId),
    onSuccess: (_, folderId) => {
      queryClient.invalidateQueries({ queryKey: ["document-folders"] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast.success("Folder deleted successfully");
      if (options.onDeleteSuccess) {
        options.onDeleteSuccess(folderId);
      }
    },
    onError: (error) => {
      console.error("Error deleting folder:", error);
      toast.error("Failed to delete folder");
    },
  });

  // Mutation to reorder folders
  const reorderFoldersMutation = useMutation({
    mutationFn: (folderIds: string[]) => FolderService.reorderFolders(folderIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document-folders"] });
    },
    onError: (error) => {
      console.error("Error reordering folders:", error);
      toast.error("Failed to reorder folders");
    },
  });

  // Mutation to move document to folder
  const moveDocumentToFolderMutation = useMutation({
    mutationFn: ({ documentId, folderId }: { documentId: string; folderId: string | null }) =>
      FolderService.moveDocumentToFolder(documentId, folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document-folders"] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      toast.success("Document moved successfully");
    },
    onError: (error) => {
      console.error("Error moving document:", error);
      toast.error("Failed to move document");
    },
  });

  // Mutation to move multiple documents to folder
  const moveDocumentsToFolderMutation = useMutation({
    mutationFn: ({ documentIds, folderId }: { documentIds: string[]; folderId: string | null }) =>
      FolderService.moveDocumentsToFolder(documentIds, folderId),
    onSuccess: (_, { documentIds }) => {
      queryClient.invalidateQueries({ queryKey: ["document-folders"] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
      toast.success(`${documentIds.length} document(s) moved successfully`);
    },
    onError: (error) => {
      console.error("Error moving documents:", error);
      toast.error("Failed to move documents");
    },
  });

  return {
    // Data
    folders: foldersQuery.data || [],
    isLoading: foldersQuery.isLoading,
    isError: foldersQuery.isError,
    error: foldersQuery.error,

    // Mutations
    createFolder: createFolderMutation.mutate,
    updateFolder: updateFolderMutation.mutate,
    deleteFolder: deleteFolderMutation.mutate,
    reorderFolders: reorderFoldersMutation.mutate,
    moveDocumentToFolder: moveDocumentToFolderMutation.mutate,
    moveDocumentsToFolder: moveDocumentsToFolderMutation.mutate,

    // Mutation states
    isCreating: createFolderMutation.isPending,
    isUpdating: updateFolderMutation.isPending,
    isDeleting: deleteFolderMutation.isPending,
    isReordering: reorderFoldersMutation.isPending,
    isMoving: moveDocumentToFolderMutation.isPending || moveDocumentsToFolderMutation.isPending,

    // Refetch
    refetch: foldersQuery.refetch,
  };
};

export const useAllFolders = () => {
  return useQuery({
    queryKey: ["document-folders-all"],
    queryFn: async () => {
      const response = await ApiService.invoke<{ folders: DocumentFolder[] }>({
        resource: "document-folders",
        action: "list",
        data: { adminMode: true },
      });
      return response.folders || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

